import { Checkbox, InputAdornment, Tooltip, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { AttachMoneyOutlined, CancelOutlined, EditOutlined, PercentOutlined, SaveOutlined } from '@mui/icons-material';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { CampaignFee } from '../../../types/Campaign';
import CDCard from '../../../components/CDCard';
import { CDTextField } from '../../../components/CDTextField';
import AccessGuard from '../../../components/AccessGuard';
import { USER_GROUP_ADMIN, USER_GROUP_AGENCY } from '../../../types/User';
import { useUserContext } from '../../../hooks/Contexts/UserContext';
import CDButton from '../../../components/CDButton';
import Row from '../../../components/Row';
import FixedVariableFeeComponent from '../../agency/FixedVariableFeeComponent';

export default function CampaignFeesComponent(props: {
    campaignFee: CampaignFee;
    editMode: boolean;
    allowEditToggle: boolean;
    agencyView?: boolean;
    onChange(campaignFee: CampaignFee): void;
}) {
    const { onChange, allowEditToggle, agencyView } = props;
    const { userContext } = useUserContext();
    const [campaignFee, setCampaignFee] = useState(props.campaignFee);
    const [editMode, setEditMode] = useState(props.editMode);

    useEffect(() => {
        setCampaignFee(props.campaignFee);
    }, [props.campaignFee]);

    function handleChange(event: ChangeEvent<HTMLInputElement>) {
        const field = event.target.name;
        let value: number | string | boolean | null = event.target.value;

        if (field.startsWith('include')) {
            value = event.target.checked;
        }

        let updatedFees = { ...campaignFee, [field]: value };
        setCampaignFee(updatedFees);
        if (!allowEditToggle && editMode) {
            onChange(updatedFees);
        }
    }

    function feeChange(updatedFees: CampaignFee) {
        setCampaignFee(updatedFees);
        if (!allowEditToggle && editMode) {
            onChange(updatedFees);
        }
    }

    function handleSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        if (campaignFee.agencyFixedCPM && !campaignFee.cognitionCPM) {
            return;
        }
        onChange(campaignFee);
        setEditMode(!editMode);
    }

    return (
        <>
            <CDCard>
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2} sx={{ pt: 2 }} alignItems="center">
                        {agencyView && (
                            <>
                                <Grid size={12}>
                                    <Typography variant="body1" fontWeight={500}>
                                        Agency Fees
                                    </Typography>
                                </Grid>

                                <FixedVariableFeeComponent
                                    label="Video"
                                    allowVariable={true}
                                    fixedFee={campaignFee.videoFixedCPM}
                                    variableFee={campaignFee.videoAgencyFee}
                                    incrementalCPM={campaignFee.videoIncrementalCPM}
                                    editMode={editMode}
                                    onChange={(fixed, variable, incremental) => {
                                        feeChange({
                                            ...campaignFee,
                                            videoFixedCPM: fixed,
                                            videoAgencyFee: variable,
                                            videoIncrementalCPM: incremental,
                                        });
                                    }}
                                />

                                <Grid size={3} />

                                <FixedVariableFeeComponent
                                    label="Online Video"
                                    allowVariable={true}
                                    fixedFee={campaignFee.onlineVideoFixedCPM}
                                    variableFee={campaignFee.onlineVideoAgencyFee}
                                    incrementalCPM={campaignFee.onlineVideoIncrementalCPM}
                                    editMode={editMode}
                                    onChange={(fixed, variable, incremental) => {
                                        feeChange({
                                            ...campaignFee,
                                            onlineVideoFixedCPM: fixed,
                                            onlineVideoAgencyFee: variable,
                                            onlineVideoIncrementalCPM: incremental,
                                        });
                                    }}
                                />

                                <Grid size={3} />

                                <FixedVariableFeeComponent
                                    label="Streaming TV"
                                    allowVariable={true}
                                    fixedFee={campaignFee.streamingTvFixedCPM}
                                    variableFee={campaignFee.streamingTvAgencyFee}
                                    incrementalCPM={campaignFee.streamingTvIncrementalCPM}
                                    editMode={editMode}
                                    onChange={(fixed, variable, incremental) => {
                                        feeChange({
                                            ...campaignFee,
                                            streamingTvFixedCPM: fixed,
                                            streamingTvAgencyFee: variable,
                                            streamingTvIncrementalCPM: incremental,
                                        });
                                    }}
                                />

                                <Grid size={3} />

                                <FixedVariableFeeComponent
                                    label="Display"
                                    allowVariable={true}
                                    fixedFee={campaignFee.displayFixedCPM}
                                    variableFee={campaignFee.displayAgencyFee}
                                    incrementalCPM={campaignFee.displayIncrementalCPM}
                                    editMode={editMode}
                                    onChange={(fixed, variable, incremental) => {
                                        feeChange({
                                            ...campaignFee,
                                            displayFixedCPM: fixed,
                                            displayAgencyFee: variable,
                                            displayIncrementalCPM: incremental,
                                        });
                                    }}
                                />

                                <Grid size={3} />

                                <FixedVariableFeeComponent
                                    label="Audio"
                                    allowVariable={true}
                                    fixedFee={campaignFee.audioFixedCPM}
                                    variableFee={campaignFee.audioAgencyFee}
                                    incrementalCPM={campaignFee.audioIncrementalCPM}
                                    editMode={editMode}
                                    onChange={(fixed, variable, incremental) => {
                                        feeChange({
                                            ...campaignFee,
                                            audioFixedCPM: fixed,
                                            audioAgencyFee: variable,
                                            audioIncrementalCPM: incremental,
                                        });
                                    }}
                                />

                                <Grid size={3} />
                            </>
                        )}

                        {!agencyView && (
                            <>
                                <Grid size={12}>
                                    <Typography variant="body1" fontWeight={500}>
                                        Agency Fees
                                    </Typography>
                                </Grid>

                                <Grid size={3}>{campaignFee.agencyFixedCPM ? 'Fixed CPM' : 'Agency Fee'}</Grid>
                                <Grid size={2}>
                                    {(!!campaignFee.agencyFixedCPM && (
                                        <>
                                            <CDTextField
                                                name="agencyFixedCPM"
                                                value={campaignFee.agencyFixedCPM}
                                                fullWidth
                                                onChange={handleChange}
                                                disabled={!editMode}
                                                slotProps={{
                                                    input: {
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <AttachMoneyOutlined
                                                                    fontSize="small"
                                                                    color={editMode ? 'primary' : 'action'}
                                                                />
                                                            </InputAdornment>
                                                        ),
                                                        inputMode: 'text',
                                                    },
                                                }}
                                            />
                                        </>
                                    )) || (
                                        <CDTextField
                                            name="agencyFee"
                                            value={campaignFee.agencyFee}
                                            fullWidth
                                            onChange={handleChange}
                                            disabled={!editMode}
                                            slotProps={{
                                                input: {
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <PercentOutlined
                                                                fontSize="small"
                                                                color={editMode ? 'primary' : 'action'}
                                                            />
                                                        </InputAdornment>
                                                    ),
                                                    inputMode: 'text',
                                                },
                                            }}
                                        />
                                    )}
                                </Grid>

                                <Grid size={7}>
                                    <Row>
                                        <div style={{ flex: 1 }} />
                                        {allowEditToggle && !editMode && (
                                            <CDButton
                                                startIcon={<EditOutlined />}
                                                onClick={() => setEditMode(!editMode)}
                                            >
                                                Edit
                                            </CDButton>
                                        )}
                                        {allowEditToggle && editMode && (
                                            <>
                                                <CDButton
                                                    startIcon={<CancelOutlined color="error" />}
                                                    onClick={() => {
                                                        setCampaignFee(props.campaignFee);
                                                        setEditMode(!editMode);
                                                    }}
                                                >
                                                    Cancel
                                                </CDButton>
                                                <CDButton type="submit" startIcon={<SaveOutlined />} onClick={() => {}}>
                                                    Save
                                                </CDButton>
                                            </>
                                        )}
                                    </Row>
                                </Grid>
                                {!!campaignFee.agencyFixedCPM && (
                                    <>
                                        <Grid size={3}>Agency Incremental CPM</Grid>
                                        <Grid size={2}>
                                            <CDTextField
                                                name="agencyIncrementalCPM"
                                                value={campaignFee.agencyIncrementalCPM}
                                                fullWidth
                                                onChange={handleChange}
                                                disabled={!editMode}
                                                slotProps={{
                                                    input: {
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <AttachMoneyOutlined
                                                                    fontSize="small"
                                                                    color={editMode ? 'primary' : 'action'}
                                                                />
                                                            </InputAdornment>
                                                        ),
                                                        inputMode: 'text',
                                                    },
                                                }}
                                            />
                                        </Grid>
                                        <Grid size={5} />

                                        <Grid size={3}>DSP CPM Cost</Grid>
                                        <Grid size={2}>
                                            <CDTextField
                                                name="cognitionCPM"
                                                value={campaignFee.cognitionCPM}
                                                fullWidth
                                                required={true}
                                                onChange={handleChange}
                                                disabled={!editMode}
                                                slotProps={{
                                                    input: {
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <AttachMoneyOutlined
                                                                    fontSize="small"
                                                                    color={editMode ? 'primary' : 'action'}
                                                                />
                                                            </InputAdornment>
                                                        ),
                                                        inputMode: 'text',
                                                    },
                                                }}
                                            />
                                        </Grid>
                                    </>
                                )}
                            </>
                        )}

                        <Grid size={12} />
                        <AccessGuard
                            accessGroup={`${USER_GROUP_ADMIN},${USER_GROUP_AGENCY}`}
                            condition={() => userContext.isAdmin() || userContext.isOpsAdmin()}
                        >
                            <>
                                <Grid size={12}>
                                    <Typography variant="body1" fontWeight={500}>
                                        Cognition Digital Fee Structure
                                    </Typography>
                                </Grid>

                                <Grid size={3}>CD Platform Fee</Grid>
                                <Grid size={2}>
                                    <CDTextField
                                        name="cdPlatformFee"
                                        value={campaignFee.cdPlatformFee}
                                        fullWidth
                                        onChange={handleChange}
                                        disabled={!editMode || userContext.isOpsAdmin()}
                                        slotProps={{
                                            input: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <AttachMoneyOutlined
                                                            fontSize="small"
                                                            color={editMode ? 'primary' : 'action'}
                                                        />
                                                    </InputAdornment>
                                                ),
                                                inputMode: 'text',
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid size={3}>
                                    {editMode && (
                                        <Tooltip title="Include in Spend / Budget">
                                            <span>
                                                <Checkbox
                                                    name="includeCdPlatformFee"
                                                    checked={campaignFee.includeCdPlatformFee}
                                                    onChange={handleChange}
                                                    disabled={!editMode || userContext.isOpsAdmin()}
                                                />
                                            </span>
                                        </Tooltip>
                                    )}
                                    {!editMode && (
                                        <span>
                                            {(campaignFee.includeCdPlatformFee ? 'Included in' : ' Excluded from ') +
                                                ' Spend / Budget'}
                                        </span>
                                    )}
                                </Grid>
                                <Grid size={4} />
                                <Grid size={3}>Amazon Passthrough Upcharge</Grid>
                                <Grid size={2}>
                                    <CDTextField
                                        name="amazonPassthroughUpcharge"
                                        value={campaignFee.amazonPassthroughUpcharge}
                                        fullWidth
                                        onChange={handleChange}
                                        disabled={!editMode || userContext.isOpsAdmin()}
                                        slotProps={{
                                            input: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <PercentOutlined
                                                            fontSize="small"
                                                            color={editMode ? 'primary' : 'action'}
                                                        />
                                                    </InputAdornment>
                                                ),
                                                inputMode: 'text',
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid size={3}>
                                    {editMode && (
                                        <Tooltip title="Include in Spend / Budget">
                                            <span>
                                                <Checkbox
                                                    name="includeAmazonPassthroughUpcharge"
                                                    checked={campaignFee.includeAmazonPassthroughUpcharge}
                                                    onChange={handleChange}
                                                    disabled={!editMode || userContext.isOpsAdmin()}
                                                />
                                            </span>
                                        </Tooltip>
                                    )}
                                    {!editMode && campaignFee.amazonPassthroughUpcharge && (
                                        <span>
                                            {(campaignFee.includeAmazonPassthroughUpcharge
                                                ? 'Included in'
                                                : ' Excluded from ') + ' Spend / Budget'}
                                        </span>
                                    )}
                                </Grid>
                                <Grid size={3} />

                                <Grid size={3}>CD Managed Service</Grid>
                                <Grid size={2}>
                                    <CDTextField
                                        name="cdManagedService"
                                        value={campaignFee.cdManagedService}
                                        fullWidth
                                        onChange={handleChange}
                                        disabled={!editMode || userContext.isOpsAdmin()}
                                        slotProps={{
                                            input: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <PercentOutlined
                                                            fontSize="small"
                                                            color={editMode ? 'primary' : 'action'}
                                                        />
                                                    </InputAdornment>
                                                ),
                                                inputMode: 'text',
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid size={7}></Grid>

                                <Grid size={3}>
                                    {editMode
                                        ? 'Other Fee'
                                        : campaignFee.otherFeeLabel
                                        ? campaignFee.otherFeeLabel
                                        : 'Other Fee'}
                                </Grid>

                                <Grid size={2}>
                                    <CDTextField
                                        name="otherFee"
                                        value={campaignFee.otherFee}
                                        fullWidth
                                        onChange={handleChange}
                                        disabled={!editMode || userContext.isOpsAdmin()}
                                        slotProps={{
                                            input: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <AttachMoneyOutlined
                                                            fontSize="small"
                                                            color={editMode ? 'primary' : 'action'}
                                                        />
                                                    </InputAdornment>
                                                ),
                                                inputMode: 'text',
                                            },
                                        }}
                                    />
                                </Grid>
                                {editMode && (
                                    <>
                                        <Grid size={1}>
                                            {editMode && (
                                                <Tooltip title="Include in Spend / Budget">
                                                    <span>
                                                        <Checkbox
                                                            name="includeOtherFee"
                                                            checked={campaignFee.includeOtherFee}
                                                            onChange={handleChange}
                                                            disabled={!editMode || userContext.isOpsAdmin()}
                                                        />
                                                    </span>
                                                </Tooltip>
                                            )}
                                        </Grid>
                                        <Grid size={2}>{editMode && 'Other Fee Label'}</Grid>
                                        <Grid size={3}>
                                            {editMode && (
                                                <CDTextField
                                                    name="otherFeeLabel"
                                                    value={campaignFee.otherFeeLabel}
                                                    fullWidth
                                                    onChange={handleChange}
                                                    disabled={!editMode || userContext.isOpsAdmin()}
                                                    slotProps={
                                                        {
                                                            // maxLength: 50,
                                                        }
                                                    }
                                                />
                                            )}
                                        </Grid>
                                        <Grid size={1} />
                                    </>
                                )}
                                {!editMode && campaignFee.otherFee && (
                                    <>
                                        <Grid size={5}>
                                            <span>
                                                {(campaignFee.includeOtherFee ? 'Included in' : ' Excluded from ') +
                                                    ' Spend / Budget'}
                                            </span>
                                        </Grid>
                                    </>
                                )}

                                {agencyView && (
                                    <>
                                        <Grid size={12}>
                                            <Typography variant="body1" fontWeight={500}>
                                                DV360 Passthrough Upcharge
                                            </Typography>
                                        </Grid>

                                        <Grid size={3}>YouTube</Grid>
                                        <Grid size={2}>
                                            <CDTextField
                                                name="dv360PassthroughUpchargeYouTube"
                                                value={campaignFee.dv360PassthroughUpchargeYouTube}
                                                fullWidth
                                                onChange={handleChange}
                                                disabled={!editMode || userContext.isOpsAdmin()}
                                                slotProps={{
                                                    input: {
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <PercentOutlined
                                                                    fontSize="small"
                                                                    color={editMode ? 'primary' : 'action'}
                                                                />
                                                            </InputAdornment>
                                                        ),
                                                        inputMode: 'text',
                                                    },
                                                }}
                                            />
                                        </Grid>
                                        <Grid size={3}>
                                            {editMode && (
                                                <Tooltip title="Include in Spend / Budget">
                                                    <span>
                                                        <Checkbox
                                                            name="includeDv360PassthroughUpchargeYouTube"
                                                            checked={campaignFee.includeDv360PassthroughUpchargeYouTube}
                                                            onChange={handleChange}
                                                            disabled={!editMode || userContext.isOpsAdmin()}
                                                        />
                                                    </span>
                                                </Tooltip>
                                            )}
                                            {!editMode && campaignFee.dv360PassthroughUpchargeYouTube && (
                                                <span>
                                                    {(campaignFee.includeDv360PassthroughUpchargeYouTube
                                                        ? 'Included in'
                                                        : ' Excluded from ') + ' Spend / Budget'}
                                                </span>
                                            )}
                                        </Grid>
                                        <Grid size={4} />

                                        <Grid size={3}>Other</Grid>
                                        <Grid size={2}>
                                            <CDTextField
                                                name="dv360PassthroughUpchargeOther"
                                                value={campaignFee.dv360PassthroughUpchargeOther}
                                                fullWidth
                                                onChange={handleChange}
                                                disabled={!editMode || userContext.isOpsAdmin()}
                                                slotProps={{
                                                    input: {
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <PercentOutlined
                                                                    fontSize="small"
                                                                    color={editMode ? 'primary' : 'action'}
                                                                />
                                                            </InputAdornment>
                                                        ),
                                                        inputMode: 'text',
                                                    },
                                                }}
                                            />
                                        </Grid>
                                        <Grid size={3}>
                                            {editMode && (
                                                <Tooltip title="Include in Spend / Budget">
                                                    <span>
                                                        <Checkbox
                                                            name="includeDv360PassthroughUpchargeOther"
                                                            checked={campaignFee.includeDv360PassthroughUpchargeOther}
                                                            onChange={handleChange}
                                                            disabled={!editMode || userContext.isOpsAdmin()}
                                                        />
                                                    </span>
                                                </Tooltip>
                                            )}
                                            {!editMode && campaignFee.dv360PassthroughUpchargeOther && (
                                                <span>
                                                    {(campaignFee.includeDv360PassthroughUpchargeOther
                                                        ? 'Included in'
                                                        : ' Excluded from ') + ' Spend / Budget'}
                                                </span>
                                            )}
                                        </Grid>
                                        <Grid size={4} />
                                    </>
                                )}
                            </>
                        </AccessGuard>
                    </Grid>
                </form>
            </CDCard>
        </>
    );
}
